import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Check, Download, MoreHorizontal, Share2, Star, StickyNote } from "lucide-react";
import "../Files/Files.scss";

const imageTypes = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"];
const videoTypes = ["mp4", "mov", "avi", "webm", "mkv"];

const fileExtension = (name = "") => {
  const extension = name.split(".").pop();
  return extension && extension !== name ? extension.toLowerCase() : "file";
};

const formatSize = (bytes) => {
  if (typeof bytes !== "number" || bytes < 0) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[index]}`;
};

const FilePreview = ({ file }) => {
  const extension = fileExtension(file?.title);
  const url = file?.content?.imageUrl?.[0]?.url;
  if (url && imageTypes.includes(extension)) {
    return <img className="files-media-preview files-media-preview--large" src={url} alt={file?.title || "File preview"} />;
  }
  if (url && videoTypes.includes(extension)) {
    return <span className="files-media-preview files-media-preview--large files-media-preview--video"><video src={url} muted playsInline preload="metadata" aria-label={`${file?.title || "File"} video preview`} /><span className="files-media-preview__play" aria-hidden="true" /></span>;
  }
  const tones = {
    pdf: "pdf", doc: "document", docx: "document", rtf: "document",
    xls: "sheet", xlsx: "sheet", csv: "sheet", ppt: "presentation", pptx: "presentation", key: "presentation",
    zip: "archive", rar: "archive", "7z": "archive", tar: "archive", gz: "archive",
    mp3: "audio", wav: "audio", aac: "audio", m4a: "audio", ogg: "audio",
    js: "code", jsx: "code", ts: "code", tsx: "code", html: "code", css: "code", json: "code", py: "code", php: "code",
  };
  const tone = tones[extension] || (imageTypes.includes(extension) ? "image" : videoTypes.includes(extension) ? "video" : "generic");
  return (
    <span className={`files-type-icon files-type-icon--${tone} files-type-icon--large`} aria-label={`${extension.toUpperCase()} file`}>
      <svg viewBox="0 0 40 52" fill="none" aria-hidden="true">
        <path className="files-type-icon__paper" d="M4 4C4 1.79086 5.79086 0 8 0H26L36 10V48C36 50.2091 34.2091 52 32 52H8C5.79086 52 4 50.2091 4 48V4Z" stroke="currentColor" strokeWidth="2" />
        <path d="M26 0V6C26 8.20914 27.7909 10 30 10H36" stroke="currentColor" strokeWidth="2" />
        <line x1="10" y1="22" x2="30" y2="22" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="28" x2="26" y2="28" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="34" x2="28" y2="34" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
      </svg>
      <span>{extension.toUpperCase().slice(0, 5)}</span>
    </span>
  );
};

export default function DrawerFileCard({ file, selected, onSelect, onStar, onShare, onDownload, onOpen }) {
  const note = String(file?.note || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const firstTag = Array.isArray(file?.tags) ? file.tags[0] : null;
  const tag = typeof firstTag === "string" ? firstTag : firstTag?.name;
  const date = file?.createdAt && !Number.isNaN(new Date(file.createdAt).getTime())
    ? new Date(file.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  return (
    <article className={`files-grid-card ${selected ? "is-selected" : ""}`} onClick={() => onOpen(file)}>
      <div className="files-grid-card__tools">
        <label className={`files-checkbox ${selected ? "is-selected" : ""}`} onClick={(event) => event.stopPropagation()}>
          <input type="checkbox" value={file?._id} checked={selected} onChange={onSelect} aria-label={`Select ${file?.title || "file"}`} />
          <span>{selected && <Check size={12} strokeWidth={3} />}</span>
        </label>
        <button type="button" className={`files-star ${file?.starred ? "files-star--active" : ""}`} onClick={(event) => { event.stopPropagation(); onStar(file); }} aria-label={file?.starred ? "Remove star" : "Star file"}>
          <Star size={17} fill={file?.starred ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="files-grid-card__preview"><FilePreview file={file} /></div>
      <div className="files-grid-card__body">
        <strong title={file?.title}>{file?.title || "Untitled file"}</strong>
        <span>{formatSize(file?.sizeInBytes)} · {date}</span>
        <div>
          <span className={`files-tag ${tag ? "" : "files-tag--empty"}`}>{tag || "No tags"}</span>
          {note && <span className="files-note-indicator" title={note}><StickyNote size={16} /></span>}
          <Menu as="div" className="files-action-menu" onClick={(event) => event.stopPropagation()}>
            <Menu.Button className="files-icon-button files-icon-button--quiet" aria-label={`Actions for ${file?.title || "file"}`}><MoreHorizontal size={18} /></Menu.Button>
            <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
              <Menu.Items className="files-action-menu__items">
                <Menu.Item>{({ active }) => <button className={active ? "is-active" : ""} onClick={() => onDownload(file)}><Download size={16} /> Download</button>}</Menu.Item>
                <Menu.Item>{({ active }) => <button className={active ? "is-active" : ""} onClick={() => onShare(file)}><Share2 size={16} /> Share</button>}</Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </article>
  );
}
